import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ProfileModal from './ProfileModal';
import CountUp from './CountUp';

const KICK_LOGO =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCY9ZX6i6-nqWMl_HoYCGu3NmlIZHlpHNJQl1ZTBFHIr1DMbjATqG3a9ffQHsD-TzcRpmqCHC4he4lOnS6rbPJYDw4js97M-b2-vCrjW7oFmr_ojKI7SbsbuRBu45pSugYvQEIA4l_11YVuD-qdVZ-N5E6koji_Wnxcgkv_tiAT8HJ6UKmmwqawcniLljc-1FguBdvyr87_lVpdmyuHd1RZ4KiMMyKwmYNLzF-YGLk6bpNXALEty2vgAPVpDGaJk9Mdlg';

const LINKS = [
  { to: '/', label: 'HOME', end: true },
  { to: '/leaderboards', label: 'LEADERBOARDS' },
  { to: '/benefits', label: 'BENEFITS' },
  { to: '/raffles', label: 'RAFFLES' },
  { to: '/store', label: 'STORE' },
  { to: '/cases', label: 'CASES' },
  { to: '/tts', label: 'TTS' },
];

function Avatar({ user, size }) {
  const [broken, setBroken] = useState(false);
  const px = size + 'px';
  if (user.avatar_url && !broken) {
    return (
      <img
        src={user.avatar_url}
        alt={user.username}
        onError={() => setBroken(true)}
        className="rounded-full border border-[#2c2c38] flex-shrink-0 object-cover"
        style={{ width: px, height: px }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-[#2a2a36] to-[#16161e] border border-[#2c2c38] flex items-center justify-center font-black text-[#c9c9d6] flex-shrink-0"
      style={{ width: px, height: px, fontSize: Math.round(size * 0.42) }}
    >
      {user.username[0]?.toUpperCase()}
    </div>
  );
}

function AuthSlot() {
  const { user, loading, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const wrapRef = useRef(null);

  // Other pages (Benefits) open Settings with window.dispatchEvent(new Event('open-profile')).
  useEffect(() => {
    const open = () => setProfileOpen(true);
    window.addEventListener('open-profile', open);
    return () => window.removeEventListener('open-profile', open);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function onOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [menuOpen]);

  if (loading) return null;
  if (user) {
    return (
      <>
        <div className="relative flex-shrink-0" ref={wrapRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2.5 pl-1.5 pr-3.5 py-1.5 rounded-full bg-[#12121a] border border-[#1e1e28] cursor-pointer hover:border-[#2c2c38] transition-colors"
          >
            <Avatar user={user} size={40} />
            <div className="leading-tight text-left">
              <div className="text-sm font-bold">{user.username}</div>
              <div className="font-mono-brand text-[11px] text-[#F6BB42]">🪙 <CountUp value={user.points} /></div>
            </div>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-[#24242e] bg-[#0e0e14] shadow-2xl overflow-hidden z-50 dropdown-pop origin-top-right">
              <div className="flex items-center gap-3.5 p-4">
                <Avatar user={user} size={52} />
                <div className="leading-tight">
                  <div className="text-base font-bold text-white">{user.username}</div>
                  <div className="text-xs text-[#888899]"><CountUp value={user.points} /> points</div>
                </div>
              </div>
              <div className="border-t border-[#1e1e28] py-1.5">
                <button
                  onClick={() => {
                    setProfileOpen(true);
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-white/85 hover:bg-white/5 cursor-pointer"
                >
                  Settings
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-[#ff544a] hover:bg-white/5 cursor-pointer"
                >
                  Log out
                </button>
              </div>
            </div>
          )}
        </div>
        {profileOpen && <ProfileModal onClose={() => setProfileOpen(false)} />}
      </>
    );
  }
  return (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        login();
      }}
      className="shine-hover hover:scale-[1.03] transition-transform flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-4 sm:py-2"
      style={{
        background: '#53FC18',
        color: '#0B0B14',
        borderRadius: 8,
        fontWeight: 800,
        letterSpacing: '0.02em',
        boxShadow: '0 2px 12px rgba(83,252,24,0.35)',
        transition: 'transform .15s ease, box-shadow .15s ease',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <img src={KICK_LOGO} alt="Kick" className="flex-shrink-0" style={{ width: 16, height: 16, objectFit: 'contain' }} />
      <span className="text-[11px] sm:text-[13px]">
        <span className="sm:hidden">Sign in</span>
        <span className="hidden sm:inline">Sign in with Kick</span>
      </span>
    </a>
  );
}

export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // A nav bar that looks identical at the top of the page and ten screens
  // down gives up a free "you've moved" cue. Toggling a slightly darker
  // background + a soft shadow past a small threshold is a cheap, common
  // polish signal (most sites do this) and costs nothing when idle since
  // it's a passive scroll listener, not per-frame work.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`backdrop-blur-xl border-b fixed top-0 w-full z-50 h-16 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled ? 'bg-black/95 border-[#ff544a]/40 shadow-[0_4px_24px_rgba(0,0,0,0.45)]' : 'bg-black/90 border-[#ff544a]/30'
        }`}
      >
        <div className="flex justify-between items-center h-full px-6 max-w-[1200px] mx-auto">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="text-[#ff544a] cursor-pointer active:scale-95 transition-transform md:hidden p-1"
              aria-label="Open Navigation Menu"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            <NavLink
              to="/"
              className="font-display-lg text-lg sm:text-2xl font-black italic text-[#ff544a] drop-shadow-[0_0_8px_rgba(255,84,74,0.6)] flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              FREEMANSLIVE
            </NavLink>
          </div>

          <div className="flex items-center gap-6">
            <nav className="hidden md:flex items-center gap-8 text-sm tracking-wider">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `transition-colors duration-200 cursor-pointer font-bold ${
                      isActive ? 'text-[#ff544a] pb-1 border-b-2 border-[#ff544a]' : 'text-white/70 hover:text-[#ff544a]'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <AuthSlot />
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
          <div className="relative w-64 h-full bg-[#0B0B14] border-l border-[#ff544a]/20 p-8 flex flex-col gap-8 z-10 shadow-2xl">
            <div className="flex justify-between items-center">
              <div className="font-display-lg text-xl font-black italic text-[#ff544a]">FREEMANSLIVE</div>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="text-white/70 hover:text-white p-1"
                aria-label="Close menu"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <nav className="flex flex-col gap-6 text-sm tracking-wider">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `text-left font-bold transition-colors ${
                      isActive ? 'text-[#ff544a] border-b-2 border-[#ff544a] w-fit pb-1' : 'text-white/70 hover:text-[#ff544a]'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
